import {RegistryLabelingStrategy, TagSchema} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {
  GenericStructuredTableColumn,
  LegacyStructuredTableRow,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {CellColors} from 'notebook/tables/constants/CellConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {Table, TableData, TableOpDiffMap} from 'notebook/tables/TableConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

export enum RegistrationTableType {
  BATCH = 'BATCH',
  FILE = 'FILE',
}
export const FIELD_TYPES_BY_REGISTRATION_TABLE_TYPE = {
  [RegistrationTableType.BATCH]: FIELD_TYPES.BATCH_LINK,
  [RegistrationTableType.FILE]: FIELD_TYPES.BIOENTITY_LINK,
};

type GenericRegistrationTableBase<TIds> = {
  name: string;
  labelingStrategy: RegistryLabelingStrategy | null;
  tagSchemaId: string;
  tagSchema: TagSchema | null;
  batchSchema: BatchSchema | null;
  isWatchingInputTable?: boolean;
  tableType: RegistrationTableType;
  provenanceUuid: string;
  columns: Array<GenericStructuredTableColumn<TIds>>;
  // The validity of a rows value depends on whether it's a RegistrationTable or a RegistrationTableConfig.
  // See RegistrationTablePatcher and RegistrationTableConfigPatcher on the server for details.
  rows: Array<LegacyStructuredTableRow>;
} & NLOId<TIds>;

export type RegistrationTableBase = GenericRegistrationTableBase<ClientId>;

export type GenericRegistrationTable<TIds> = GenericRegistrationTableBase<TIds> & {
  registrationRecordListId: string | null;
  type: NoteLinkedObjectType.REGISTRATION_TABLE;
};

export type RegistrationTable = GenericRegistrationTable<ClientId>;
export type ToServerRegistrationTable = GenericRegistrationTable<ToServerId>;
export type FromServerRegistrationTable = GenericRegistrationTable<FromServerId>;

export type GenericRegistrationTableConfig<TIds> = GenericRegistrationTableBase<TIds> & {
  type: NoteLinkedObjectType.REGISTRATION_TABLE_CONFIG;
};

export type RegistrationTableConfig = GenericRegistrationTableConfig<ClientId>;
export type ToServerRegistrationTableConfig = GenericRegistrationTableConfig<ToServerId>;
export type FromServerRegistrationTableConfig = GenericRegistrationTableConfig<FromServerId>;

// Keep in sync with benchling/api/eln/registration_tables.py
export enum RegistrationOperationType {
  REGISTER_NEW_FILE_WITH_BATCH = 'REGISTER_NEW_FILE_WITH_BATCH',
  ADD_BATCH_TO_EXISTING_FILE = 'ADD_BATCH_TO_EXISTING_FILE',
  REGISTER_NEW_FILE = 'REGISTER_NEW_FILE',
  ADD_EXISTING_FILE = 'ADD_EXISTING_FILE',
}

// Keep in sync with benchling/api/eln/registration_tables.py
export interface RegistrationOperation {
  operationType: RegistrationOperationType;
  name?: string;
  bases?: string;
  smiles?: string;
  stageRunId: string | null;
  fileId?: string;
  tagsDict: {[id: string]: Partial<SchemaTag>};
  batchTagsDict: {[id: string]: Partial<SchemaTag>};
}

export interface RegistrationTableUpdateParams {
  table?: TableData;
  item?: RegistrationTable | RegistrationTableConfig;
  isWatchingInputTable?: boolean;
  structuredTableColumns?: Array<StructuredTableColumn>;
  rows?: Array<LegacyStructuredTableRow>;
}

export interface RegistrationTableBaseProps extends MediocreSearchResultProps {
  icon: BenchlingIconName;
  isMaximized?: boolean;
  tableTypeLabel: string;
  tableName: string;
  rows: Array<LegacyStructuredTableRow>;
  labelingStrategy: RegistryLabelingStrategy | null;
  structuredTableColumns: Array<StructuredTableColumn>;
  provenanceUuid: string;
  isWatchingInputTable?: boolean;
  tableType: RegistrationTableType;
  // Props for TableEditable
  cid: string;
  indentation: number;
  index: number;
  metadataForRestore: MetadataForRestore<[number, number]> | null;
  mouseDownHandler: (index: number, e: BasicEvent) => void;
  mouseOverHandler: (index: number, e: BasicEvent) => void;
  updateHandler: (
    index: number,
    content: RegistrationTableUpdateParams,
    selectors: undefined,
    tableOp?: TableOpDiffMap
  ) => void;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  computedTable: Array<Array<EvaluatedCell>>;
  cellColors: CellColors;
  multiEditing: boolean;
  tableNamespace: string;
  table: Table;
  showExcessLabels: boolean;
  tagSchema: TagSchema;
  batchSchema: BatchSchema | null;
}
